import moment from 'moment';
import SparkMD5 from 'spark-md5';

const rangeOfFullYearsWithFuture = (start, end) => {
  const fullYears = (end.getTime() - start.getTime()) / 31536000000;
  return Array(end.getFullYear() - start.getFullYear() + 1)
    .fill(start.getFullYear())
    .map((year, index) => year + index)
    .filter((year, index) => index <= fullYears)
    .map((year) => ({ label: year, value: year }));
};

const rangeOfFullYears = (startYear = 1920, type = 'forward') => {
  let currentYear = new Date().getFullYear();
  const yearRange = [];

  if (type === 'forward') {
    while (startYear <= currentYear) {
      const year = startYear++;
      yearRange.push({ label: year, value: year });
    }
  } else {
    while (currentYear >= startYear) {
      const year = currentYear--;
      yearRange.push({ label: year, value: year });
    }
  }

  return yearRange;
};

// const days = Array.from({length: 31}, (_, i) => i + 1).map(val => ({value:val, label: val}))

const getDaysInMonth = (month, year) => {
  // var date = new Date(year, month, 1);
  var date = new Date(Date.UTC(year, month, 1));
  var days = [];
  while (date.getUTCMonth() === month) {
    days.push(date.getUTCDate());
    date.setUTCDate(date.getUTCDate() + 1);
  }
  let finalDaysData = days.map((val) => ({ value: val, label: val }));

  return finalDaysData;
};

const meetingsByDate = (meetings) =>
  meetings?.reduce((acc, meeting) => {
    const date = new Date(meeting?.start_time);
    const key = isToday(date)
      ? 'Today'
      : isTomorrow(date)
      ? 'Tomorrow'
      : moment(date).format('ddd MMM D, YYYY');
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(meeting);
    return acc;
  }, {});

const todayDate = moment().format('YYYY-MM-DD');
const presentTime = moment();
const tomorrowDate = moment().startOf('day').add(1, 'day').format('YYYY-MM-DD');
const endOfToday = moment().endOf('day');

const isToday = (meetingDate) => {
  return moment(meetingDate).format('YYYY-MM-DD') === todayDate;
};

const isTomorrow = (meetingDate) => {
  return moment(meetingDate).format('YYYY-MM-DD') === tomorrowDate;
};

const isAfterTomorrow = (meetingDate) => {
  return moment(meetingDate).isAfter(tomorrowDate);
};

const isAfterPresentTimeAndToday = (meetingDate) => {
  return (
    moment(meetingDate).isAfter(presentTime) &&
    moment(meetingDate).isBefore(endOfToday)
  );
};

const isAfterPresentTime = (meetingDate) => {
  return moment(meetingDate).isAfter(presentTime);
};

const isBeforeToday = (meetingDate) => {
  return moment(meetingDate).isBefore(todayDate);
};

/**
 * Trims a long string and adds ellipsis(...) at the end
 * @param {*} str
 * @param {*} opts {length(number), appendSuffix(boolean), suffix(string)}
 * @returns
 */
export const trimStr = (str, opts) => {
  const { length = 20, appendSuffix = true, suffix = '...' } = opts || {};
  return str?.length > length
    ? str?.substring(0, length - 1) + (appendSuffix ? suffix : '')
    : str;
};

/**
 * Convers a string to pascal case
 * @param {*} str
 * @returns
 */
export const toPascalCase = (str) => {
  return str.replace(/(\w)(\w*)/g, function (g0, g1, g2) {
    return g1.toUpperCase() + g2.toLowerCase();
  });
};

const isImageURL = (url) => {
  return /(http(s?):)([/|.|\w|\s|-])*\.(?:jpg|gif|png|jpeg)/g.test(url);
};

/**
 * Generate random ID of specific length
 * @param {*} length
 * @returns
 */
export const randomId = (length = 5) => {
  var result = '';
  var characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  var charactersLength = characters.length;
  for (var i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const truncateStringbyWords = (str, no_words) => {
  return str?.split(' ')?.splice(0, no_words)?.join(' ');
};

export function fallbackCopyTextToClipboard(text) {
  var textArea = document.createElement('textarea');
  textArea.value = text;

  // Avoid scrolling to bottom
  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.position = 'fixed';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    var successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    return successful;
  } catch (err) {
    document.body.removeChild(textArea);
    console.error('Fallback: Oops, unable to copy', err);
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
}

export async function copyTextToClipboard(text) {
  if (!navigator.clipboard) {
    return fallbackCopyTextToClipboard(text);
  }
  await navigator.clipboard.writeText(text);
  return true;
}

const countryToCurrencyMap = {
  'AF': 'AFN', // Afghanistan -> Afghani
  'EU': 'EUR', // Euro
  'AL': 'ALL', // Albania -> Lek
  'DZ': 'DZD', // Algeria -> Algerian Dinar
  'US': 'USD', // US Dollar
  'AO': 'AOA', // Angola -> Kwanza
  'XC': 'XCD', // East Caribbean Dollar
  'AR': 'ARS', // Argentina -> Argentine Peso
  'AM': 'AMD', // Armenia -> Armenian Dram
  'AW': 'AWG', // Aruba -> Aruban Florin
  'AU': 'AUD', // Australia -> Australian Dollar
  'AZ': 'AZN', // Azerbaijan -> Azerbaijan Manat
  'BS': 'BSD', // Bahamas -> Bahamian Dollar
  'BH': 'BHD', // Bahrain -> Bahraini Dinar
  'BD': 'BDT', // Bangladesh -> Taka
  'BB': 'BBD', // Barbados -> Barbados Dollar
  'BY': 'BYN', // Belarus -> Belarusian Ruble
  'BZ': 'BZD', // Belize -> Belize Dollar
  'XO': 'XOF', // CFA Franc BCEAO
  'BM': 'BMD', // Bermuda -> Bermudian Dollar
  'IN': 'INR', // India -> Indian Rupee
  'BT': 'BTN', // Bhutan -> Ngultrum
  'BO': 'BOB', // Bolivia -> Boliviano
  'BA': 'BAM', // Bosnia -> Convertible Mark
  'BW': 'BWP', // Botswana -> Pula
  'NO': 'NOK', // Norway -> Norwegian Krone
  'BR': 'BRL', // Brazil -> Brazilian Real
  'BN': 'BND', // Brunei -> Brunei Dollar
  'BG': 'BGN', // Bulgaria -> Bulgarian Lev
  'BI': 'BIF', // Burundi -> Burundi Franc
  'CV': 'CVE', // Cape Verde -> Cape Verde Escudo
  'KH': 'KHR', // Cambodia -> Riel
  'XA': 'XAF', // CFA Franc BEAC
  'CA': 'CAD', // Canada -> Canadian Dollar
  'KY': 'KYD', // Cayman Islands -> Cayman Islands Dollar
  'CL': 'CLP', // Chile -> Chilean Peso
  'CN': 'CNY', // China -> Yuan Renminbi
  'CO': 'COP', // Colombia -> Colombian Peso
  'KM': 'KMF', // Comoros -> Comoro Franc
  'CD': 'CDF', // Congo DR -> Congolese Franc
  'NZ': 'NZD', // New Zealand -> New Zealand Dollar
  'CR': 'CRC', // Costa Rica -> Costa Rican Colon
  'CU': 'CUP', // Cuba -> Cuban Peso
  'CZ': 'CZK', // Czech Republic -> Czech Koruna
  'DK': 'DKK', // Denmark -> Danish Krone
  'DJ': 'DJF', // Djibouti -> Djibouti Franc
  'DO': 'DOP', // Dominican Republic -> Dominican Peso
  'EG': 'EGP', // Egypt -> Egyptian Pound
  'SV': 'SVC', // El Salvador -> El Salvador Colon
  'ER': 'ERN', // Eritrea -> Nakfa
  'SZ': 'SZL', // Eswatini -> Lilangeni
  'ET': 'ETB', // Ethiopia -> Ethiopian Birr
  'FK': 'FKP', // Falkland Islands -> Falkland Islands Pound
  'FJ': 'FJD', // Fiji -> Fiji Dollar
  'XP': 'XPF', // CFP Franc
  'GM': 'GMD', // Gambia -> Dalasi
  'GE': 'GEL', // Georgia -> Lari
  'GH': 'GHS', // Ghana -> Ghana Cedi
  'GI': 'GIP', // Gibraltar -> Gibraltar Pound
  'GT': 'GTQ', // Guatemala -> Quetzal
  'GB': 'GBP', // UK -> Pound Sterling
  'GN': 'GNF', // Guinea -> Guinea Franc
  'GY': 'GYD', // Guyana -> Guyana Dollar
  'HT': 'HTG', // Haiti -> Gourde
  'HN': 'HNL', // Honduras -> Lempira
  'HK': 'HKD', // Hong Kong -> Hong Kong Dollar
  'HU': 'HUF', // Hungary -> Forint
  'IS': 'ISK', // Iceland -> Iceland Krona
  'ID': 'IDR', // Indonesia -> Rupiah
  'XD': 'XDR', // SDR
  'IR': 'IRR', // Iran -> Iranian Rial
  'IQ': 'IQD', // Iraq -> Iraqi Dinar
  'IL': 'ILS', // Israel -> New Israeli Sheqel
  'JM': 'JMD', // Jamaica -> Jamaican Dollar
  'JP': 'JPY', // Japan -> Yen
  'JO': 'JOD', // Jordan -> Jordanian Dinar
  'KZ': 'KZT', // Kazakhstan -> Tenge
  'KE': 'KES', // Kenya -> Kenyan Shilling
  'KP': 'KPW', // North Korea -> North Korean Won
  'KR': 'KRW', // South Korea -> Won
  'KW': 'KWD', // Kuwait -> Kuwaiti Dinar
  'KG': 'KGS', // Kyrgyzstan -> Som
  'LA': 'LAK', // Laos -> Kip
  'LB': 'LBP', // Lebanon -> Lebanese Pound
  'LS': 'LSL', // Lesotho -> Loti
  'ZA': 'ZAR', // South Africa -> Rand
  'LR': 'LRD', // Liberia -> Liberian Dollar
  'LY': 'LYD', // Libya -> Libyan Dinar
  'CH': 'CHF', // Switzerland -> Swiss Franc
  'MO': 'MOP', // Macao -> Pataca
  'MK': 'MKD', // North Macedonia -> Denar
  'MG': 'MGA', // Madagascar -> Malagasy Ariary
  'MW': 'MWK', // Malawi -> Malawi Kwacha
  'MY': 'MYR', // Malaysia -> Malaysian Ringgit
  'MV': 'MVR', // Maldives -> Rufiyaa
  // Add more as needed
};

// export const currencyFormatter = (number, currency = 'USD') => {
//   const formatter = new Intl.NumberFormat('en-US', {
//     style: 'currency',
//     currency: currency,
//     maximumFractionDigits: 0,
// 
//     // These options are needed to round to whole numbers if that's what you want.
//     //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
//     //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
//   });
//   return formatter.format(number);
// };

export const currencyFormatter = (number, currency = 'USD') => {
  if (!number && number !== 0) return '';

  let validatedCurrency = currency;

  // Convert country code to currency code
  if (currency && countryToCurrencyMap[currency.toUpperCase()]) {
    validatedCurrency = countryToCurrencyMap[currency.toUpperCase()];
  }

  try {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: validatedCurrency || 'USD',
      maximumFractionDigits: 0,
    });
    return formatter.format(number);
  } catch (error) {
    console.warn(`Invalid currency code: ${currency}, falling back to USD`);
    // Fallback to USD
    const fallbackFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });
    return fallbackFormatter.format(number);
  }
};


export const convertToSlug = (text) => {
  return text
    .toLowerCase()
    .replace(/[^\w ]+/g, '')
    .replace(/ +/g, '-');
};

// export const isHTML = (str) => {
//   const parser = new DOMParser();
//   const doc = parser.parseFromString(str, "text/html");
//   return Array.from(doc.body.childNodes).some(node => node.nodeType === 1);
// }
export const isHTML = (str) => {
  if (typeof window === 'undefined') {
    return /<[a-z/][\s\S]*>/i.test(str);
  }
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(str, "text/html");
    return Array.from(doc.body.childNodes).some(node => node.nodeType === 1);
  } catch (e) {
    return /<[a-z/][\s\S]*>/i.test(str);
  }
}

export const calculateChecksum = async (file, chunkSize) => {
  return new Promise((res, rej) => {
    let md5;
    let slice =
        File.prototype.slice ||
        File.prototype.mozSlice ||
        File.prototype.webkitSlice,
      chunks = Math.ceil(file.size / chunkSize),
      current_chunk = 0,
      spark = new SparkMD5.ArrayBuffer();

    function onload(e) {
      try {
        spark.append(e.target.result); // append chunk
        current_chunk++;
        if (current_chunk < chunks) {
          read_next_chunk();
        } else {
          md5 = spark.end();
          res({ md5, totalChunks: chunks });
        }
      } catch (e) {
        rej(e);
      }
    }

    function read_next_chunk() {
      try {
        var reader = new FileReader();
        reader.onload = onload;
        var start = current_chunk * chunkSize,
          end = Math.min(start + chunkSize, file.size);
        reader.readAsArrayBuffer(slice.call(file, start, end));
      } catch (e) {
        rej(e);
      }
    }
    read_next_chunk();
  });
};

export const postHasInsights = (post) => {
  const { like_count, dislike_count, comment_count, reposts_by } = post ?? {};
  return (
    (like_count ?? 0) + (dislike_count ?? 0) > 0 ||
    comment_count > 0 ||
    reposts_by?.length > 0
  );
};

export const cleanHTML = (str, nodes) => {
  // Do stuff here...
  function stringToHTML() {
    let parser = new DOMParser();
    let doc = parser.parseFromString(str, 'text/html');
    return doc.body;
  }

  // Convert the string to HTML
  let html = stringToHTML();
  return html;
};

export const imageLinkToFileObject = async (url, props) => {
  return new Promise((res, rej) => {
    fetch(url)
      .then(async (response) => {
        const contentType = props?.type ?? response.headers.get('content-type');
        const blob = await response.blob();
        const file = new File([blob], props?.fileName ?? randomId(), {
          contentType,
        });
        res(file);
        // access file here
      })
      .catch((e) => {
        rej(e);
      });
  });
};

export const capitalizeText = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const getFileExtension = (fileName) => {
  return fileName?.substring(fileName?.lastIndexOf('.') + 1);
};

export const getNextPageFromURL = (url) => {
  if (!url) return null;
  const url_ = new URL(url);
  const page = url_?.searchParams?.get?.('page');
  return parseInt(page, 10);
};

export const flattenQueryResults = (payload = { pages: [] }) => {
  return [].concat.apply(
    [],
    payload?.pages?.map((p) => (p?.results ? p?.results : p ?? []))
  );
};

export function getMatrixUserId(userId) {
  if (!userId) return;
  try {
    const matrixData = JSON.parse(localStorage.getItem('matrixData') || '[]');

    const localMatch = matrixData.find(
      (item) => item.connection?.requested_by === userId
    );

    const matrixUserId = localMatch?.connection?.room_detail?.users?.find(
      (user) => user.id === userId
    )?.user_id;

    return matrixUserId;
  } catch (error) {
    console.error('Error getting matrix user ID:', error);
    return;
  }
}

export const formatISODate = (isoString) => {
  const date = new Date(isoString);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};
export function getRoomIdFromLS(userId) {
  if (!userId) return;
  try {
    const matrixData = JSON.parse(localStorage.getItem('matrixData') || '[]');

    const localMatch = matrixData.find(
      (item) => item.connection?.requested_by === userId
    );
    
    const roomId = localMatch?.connection?.room_id;

    return roomId;
  } catch (error) {
    console.error('Error getting matrix user ID:', error);
    return;
  }
}



export function convertMatrixConnectionsToRooms(
  currentUserMatrixId,
  targetUserMatrixId // 👈 optional (from URL)
) {
  const matrixData = JSON.parse(localStorage.getItem('matrixData') || '[]');
  if (!Array.isArray(matrixData)) return [];

  return matrixData.map(item => {
    const { connection } = item;
    const roomDetail = connection?.room_detail ?? {};
    const users = roomDetail.users ?? [];

    let userDetailsUser = null;

    // // 1️⃣ requested_for (authoritative)
    // if (connection?.requested_for) {
    //   userDetailsUser = users.find(
    //     u => u.user_id === connection.requested_for
    //   );
    // }

    // 2️⃣ deep-link target user (URL userId)
    // if (!userDetailsUser && targetUserMatrixId) {
    //   userDetailsUser = users.find(
    //     u => u.user_id === targetUserMatrixId
    //   );
    // }

    // 3️⃣ fallback: other than current user
    if (!userDetailsUser) {
      userDetailsUser = users.find(
        u => u.user_id !== currentUserMatrixId
      );
    }

    return {
      id: connection.id,
      room_id: connection.room_id,
      room_name: roomDetail.room_name ?? '',
      type: roomDetail.type ?? 'direct',

      users: users.map(u => ({
        user_id: u.user_id,
        display_name: u.display_name,
        avatar_url: u.avatar_url ?? null,
        power_level: u.power_level ?? 0,
        invited: u.is_invited ?? false,
        presence: u.presence ?? 'offline',
        last_active_ago: u.last_active_ago ?? null,
        currently_active: u.currently_active ?? false,
        status_msg: u.status_msg ?? null
      })),

      user_details: userDetailsUser
        ? {
            user_id: userDetailsUser.user_id,
            display_name: userDetailsUser.display_name,
            avatar_url: userDetailsUser.avatar_url ?? null,
            presence: userDetailsUser.presence ?? 'offline',
            matrix_presence: 'online',
            matrix_status: null,
            is_presence_manual: false
          }
        : null,

      last_message: connection.message ?? '',
      last_message_time: connection.updated_at
        ? new Date(connection.updated_at).getTime()
        : null,
      updated_at: connection.updated_at
    };
  });
}





export function getLocalRoomId(userId) {
  if (!userId) return;
  try {
    const matrixData = JSON.parse(localStorage.getItem('matrixData') || '[]');

    const joinStatus = matrixData.find((item) =>
      item.connection?.room_detail?.users?.some(
        (user) => user.user_id === userId
      )
    )?.connection?.join_status;

    const roomIdMatch = joinStatus?.match(/room_id: ([a-f0-9-]+)/);
    const roomId = roomIdMatch ? roomIdMatch[1] : null;
    
    return roomId;
  } catch (error) {
    console.error('Error getting matrix user ID:', error);
    return;
  }
}

// export const convertMentionsIntoLinksInText = (text) => {
//   if (!text) return '';

//   let processedText = text
//     ?.replace?.('\r', '<br/>')
//     ?.replace(/\n/g, '<br/>');

//   const tags = processedText.match(/@\w+/g);

//   console.log(tags, 'asdf tags')
// }

export const replaceMentionsWithLinks = (content, mentionUsers) => {
  if (
    !content ||
    !mentionUsers ||
    !Array.isArray(mentionUsers) ||
    mentionUsers.length === 0
  ) {
    return content || '';
  }

  // Helper function to find matching user for a mention text
  const findMatchingUser = (mentionText) => {
    const words = mentionText.split(/\s+/);

    // Try matching from longest to shortest (e.g., "Temp 1 hello" -> "Temp 1" -> "Temp")
    for (let wordCount = words.length; wordCount > 0; wordCount--) {
      const testName = words.slice(0, wordCount).join(' ').toLowerCase().trim();

      // Find user with matching name
      const user = mentionUsers.find((u) => {
        if (!u?.name || !u?.slug) return false;
        const userName = u.name.toLowerCase().trim();

        // Exact match
        if (userName === testName) return true;

        // Partial match: user name starts with test name (e.g., "John Doe" matches "John")
        if (userName.startsWith(testName + ' ')) return true;

        return false;
      });

      if (user) {
        return {
          user,
          matchedName: words.slice(0, wordCount).join(' '),
        };
      }
    }

    return null;
  };

  // Find all @ mentions and replace them
  return content.replace(/@([^\n@#]+)/g, (fullMatch, mentionText) => {
    const trimmedMention = mentionText.trim();
    if (!trimmedMention) return fullMatch;

    const match = findMatchingUser(trimmedMention);

    if (match && match.user.slug) {
      // Replace only the matched portion (e.g., "@Temp 1" from "@Temp 1 hello")
      const mentionToLink = `${match.matchedName}`;
      const remainingText = fullMatch.slice(1 + mentionToLink.length); // " hello" part
      const linkHtml = `<a href="/p/${match.user.slug}" data-mention-slug="${match.user.slug}" class="mention-link" style="color:#3182ce; font-weight:500; cursor: pointer;">${mentionToLink}</a>`;

      return linkHtml + remainingText;
    }

    return fullMatch;
  });
};

export const replaceHashtagsWithLinks = (content) => {
  return content?.replace(/#([a-zA-Z0-9]+)/g, (match, tag) => {
    return `<a href=#${tag} data-hashtag="#${tag}" class="hashtag-link" style="color:#3182ce; background-color:#E6F0FF; font-weight:500; cursor: pointer; padding: 2px 6px; border-radius: 6px; display: inline-block;">${match}</a>`;
  });
};

export const convertLinksInText = (text, textColor='#3182ce') => {
  if (!text) return '';

  if (/<[a-z][\s\S]*>/i.test(text)) {
    return convertLinksInHtml(text, textColor);
  }

  // First normalize line endings
  let processedText = text
    ?.replace(/\r\n/g, '\n') // Normalize \r\n to \n first
    ?.replace(/\r/g, '\n'); // Then replace \r with \n

  processedText = processedText?.replace(/\n{3,}/g, '\n\n');
  processedText = processedText?.replace(/\n/g, '<br/>');
  processedText = processedText?.replace(/(<br\s*\/?>){3,}/gi, '<br/><br/>');

  const urlRegex =
    /(https?:\/\/[^\s<>"{}|\\^`[\]]+|www\.[^\s<>"{}|\\^`[\]]+|[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.([a-zA-Z]{2,})(\/[^\s<>"{}|\\^`[\]]*)?)/gi;

  // Replace URLs with anchor tags
  processedText = processedText.replace(urlRegex, (match) => {
    const cleanUrl = match.replace(/[,;:!?.]+$/, '');

    let href = cleanUrl;
    const urlLower = cleanUrl.toLowerCase();
    if (!urlLower.startsWith('http://') && !urlLower.startsWith('https://')) {
      href = `https://${cleanUrl}`;
    } else {
      href = urlLower.startsWith('http://')
        ? cleanUrl
        : cleanUrl.replace(/^https?:\/\//i, 'https://');
    }

    return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color: ${textColor}; text-decoration: underline;">${cleanUrl}</a>`;
  });

  return processedText;
};

export const escapeHtml = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

export const formatContent = (content, mentionUsers = []) => {
  if (!content) return '';

  // let formatted = replaceHashtagsWithLinks(content);
  let formatted = escapeHtml(content);
  formatted = replaceHashtagsWithLinks(formatted);
  formatted = replaceMentionsWithLinks(formatted, mentionUsers);
  // formatted = convertLinksInText(formatted);
  formatted = convertLinksInTextSkipExisting(formatted);
  formatted = formatted.replace(/(<br\s*\/?>){2,}/gi, '<br/>');

  return formatted;
};

// Separate function for article descriptions that handles HTML mention spans from Tiptap
export const replaceArticleMentionsWithLinks = (content, mentionUsers) => {
  if (!content) return '';

  // Handle HTML mention spans (from Tiptap editor)
  // Convert mention spans to clickable links using data attributes
  if (
    content.includes('class="mention"') ||
    content.includes("class='mention'")
  ) {
    // Replace mention spans with clickable links
    // Match: <span class="mention" ... data-slug="..." ...>@Text</span>
    // This handles nested tags like <strong><span class="mention">...</span></strong>
    content = content.replace(
      /<span([^>]*class=["'][^"']*mention[^"']*["'][^>]*)>([^<]+)<\/span>/gi,
      (match, attributes, text) => {
        // Skip if already converted to a link
        if (match.includes('mention-link') || match.includes('<a')) {
          return match;
        }
        // Extract slug from data-slug attribute
        const slugMatch = attributes.match(/data-slug=["']([^"']+)["']/i);
        if (slugMatch && slugMatch[1]) {
          const slug = slugMatch[1];
          // Remove @ symbol if present and trim
          const displayText = text.replace(/^@\s*/, '').trim();
          const linkHtml = `<a href="/p/${slug}" data-mention-slug="${slug}" class="mention-link" style="color:#3182ce; font-weight:500; cursor: pointer;">${displayText}</a>`;
          return linkHtml;
        }
        return match;
      }
    );

    // Return early if we processed HTML mentions
    return content;
  }

  // Fallback to plain text mentions if no HTML mentions found
  return replaceMentionsWithLinks(content, mentionUsers);
};

// Convert links in text but skip URLs already inside anchor tags or image tags (for article content)
const convertLinksInTextSkipExisting = (text) => {
  if (!text) return '';

  // First normalize line endings
  let processedText = text
    ?.replace(/\r\n/g, '\n') // Normalize \r\n to \n first
    ?.replace(/\r/g, '\n'); // Then replace \r with \n

  processedText = processedText?.replace(/\n{3,}/g, '\n\n');
  processedText = processedText?.replace(/\n/g, '<br/>');
  processedText = processedText?.replace(/(<br\s*\/?>){3,}/gi, '<br/><br/>');

  // Only convert URLs that are NOT already inside anchor tags or image tags
  const urlRegex =
    /(https?:\/\/[^\s<>"{}|\\^`[\]]+|www\.[^\s<>"{}|\\^`[\]]+|[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.([a-zA-Z]{2,})(\/[^\s<>"{}|\\^`[\]]*)?)/gi;

  // Split content by existing anchor tags and image tags to avoid processing URLs inside them
  // Match both <a> tags and <img> tags (including self-closing <img/>)
  // Use a more comprehensive regex to match img tags with all their attributes
  const parts = processedText.split(
    /(<a[^>]*>.*?<\/a>|<img[^>]*?(?:\s*\/>|>))/gi
  );

  processedText = parts
    .map((part) => {
      const trimmedPart = part.trim();

      // Skip parts that are already anchor tags
      if (trimmedPart.match(/^<a[^>]*>.*?<\/a>$/gis)) {
        return part;
      }

      // Skip parts that are image tags (including self-closing and regular)
      // Match <img ... /> or <img ... >
      if (trimmedPart.match(/^<img[\s\S]*?(?:\s*\/>|>)$/gi)) {
        return part;
      }

      // Process URLs only in non-anchor and non-image parts
      // Also check if URL appears to be inside an HTML attribute (between quotes after src=, href=, etc.)
      return part.replace(urlRegex, (...args) => {
        const match = args[0];
        const offset = args[args.length - 2];
        const str = typeof args[args.length - 1] === 'string' ? args[args.length - 1] : part;
        // Check if this URL is inside an HTML attribute by looking backwards
        const beforeMatch =
          typeof str === 'string' && typeof offset === 'number'
            ? str.substring(0, offset)
            : '';
        const lastQuote = beforeMatch
          ? Math.max(
              beforeMatch.lastIndexOf('src="'),
              beforeMatch.lastIndexOf("src='"),
              beforeMatch.lastIndexOf('href="'),
              beforeMatch.lastIndexOf("href='")
            )
          : -1;

        if (lastQuote !== -1 && typeof str === 'string') {
          // Found a potential attribute start, check if we're still inside it
          const afterQuote = str.substring(lastQuote);
          const isHref = afterQuote.startsWith('href=');
          const quoteIndex = isHref ? 5 : 4;
          const quoteChar = afterQuote[quoteIndex];
          const nextQuote =
            quoteChar === '"' || quoteChar === "'"
              ? afterQuote.indexOf(quoteChar, quoteIndex + 1)
              : -1;

          if (
            nextQuote !== -1 &&
            typeof offset === 'number' &&
            offset < lastQuote + nextQuote + 5
          ) {
            // We're inside an attribute value, don't convert this URL
            return match;
          }
        }

        const cleanUrl = match.replace(/[,;:!?.]+$/, '');

        let href = cleanUrl;
        const urlLower = cleanUrl.toLowerCase();
        if (
          !urlLower.startsWith('http://') &&
          !urlLower.startsWith('https://')
        ) {
          href = `https://${cleanUrl}`;
        } else {
          href = urlLower.startsWith('http://')
            ? cleanUrl
            : cleanUrl.replace(/^https?:\/\//i, 'https://');
        }

        return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color: #3182ce; text-decoration: underline;">${cleanUrl}</a>`;
      });
    })
    .join('');

  return processedText;
};

// Format function specifically for article descriptions
export const formatArticleContent = (content, mentionUsers = []) => {
  if (!content) return '';

  let formatted = replaceHashtagsWithLinks(content);
  formatted = replaceArticleMentionsWithLinks(formatted, mentionUsers);
  // Use the version that skips URLs already in anchor tags (Tiptap already converts URLs)
  formatted = convertLinksInTextSkipExisting(formatted);
  formatted = formatted.replace(/(<br\s*\/?>){2,}/gi, '<br/>');

  return formatted;
};

// export const formatJobDescription = (content) => {
//   if (!content) return '';
// 
//   // Use the same hashtag replacement function as posts
//   let formatted = replaceHashtagsWithLinks(content);
// 
//   // Convert line breaks and URLs
//   formatted = convertLinksInText(formatted);
//   formatted = formatted.replace(/(<br\s*\/?>){2,}/gi, '<br/>');
// 
//   return formatted;
// };

export const convertLinksInHtml = (html, textColor = '#3182ce') => {
  if (!html) return '';

  const parts = html.split(/(<[^>]+>)/g);
  let inAnchor = false;

  const urlRegex =
    /(https?:\/\/[^\s<>"{}|\\^`[\]]+|www\.[^\s<>"{}|\\^`[\]]+|[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.([a-zA-Z]{2,})(\/[^\s<>"{}|\\^`[\]]*)?)/gi;

  const processedParts = parts.map((part) => {
    if (part.startsWith('<') && part.endsWith('>')) {
      const lowerPart = part.toLowerCase();
      if (lowerPart.startsWith('<a')) {
        inAnchor = true;
      } else if (lowerPart === '</a>') {
        inAnchor = false;
      }
      return part;
    } else {
      if (inAnchor) {
        return part;
      }
      return part.replace(urlRegex, (match) => {
        const cleanUrl = match.replace(/[,;:!?.]+$/, '');
        let href = cleanUrl;
        const urlLower = cleanUrl.toLowerCase();
        if (!urlLower.startsWith('http://') && !urlLower.startsWith('https://')) {
          href = `https://${cleanUrl}`;
        } else {
          href = urlLower.startsWith('http://')
            ? cleanUrl
            : cleanUrl.replace(/^https?:\/\//i, 'https://');
        }
        return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color: ${textColor}; text-decoration: underline;">${cleanUrl}</a>`;
      });
    }
  });

  return processedParts.join('');
};

export const formatJobDescription = (content) => {
  if (!content) return '';

  let formatted = replaceHashtagsWithLinks(content);

  if (isHTML(content)) {
    formatted = convertLinksInHtml(formatted);
  } else {
    formatted = convertLinksInText(formatted);
    formatted = formatted.replace(/(<br\s*\/?>){2,}/gi, '<br/>');
  }

  return formatted;
};

export const isIOS = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  // return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.maxTouchPoints > 1 && /Mac/.test(navigator.userAgent));
};

export const isIphoneWithSafari = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const ua = navigator.userAgent;

  const isIPhone = /iPhone/.test(ua);

  const isSafari =
    /Safari/.test(ua) &&
    !/CriOS|Chrome|EdgiOS|FxiOS|OPiOS/.test(ua);

  return isIPhone && isSafari;
};

const copyToClipboard = async (text) => {
  if (!text) return false;

  if (typeof navigator !== 'undefined' && navigator?.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn('navigator.clipboard.writeText failed, falling back to execCommand', err);
    }
  }

  if (typeof document !== 'undefined') {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '2em';
      textArea.style.height = '2em';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';
      textArea.setAttribute('readonly', '');

      document.body.appendChild(textArea);

      if (typeof navigator !== 'undefined' && navigator?.userAgent?.match(/ipad|iphone/i)) {
        const range = document.createRange();
        range.selectNodeContents(textArea);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        textArea.setSelectionRange(0, 999999);
      } else {
        textArea.select();
      }

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return successful;
    } catch (err) {
      console.error('Fallback copy failed: ', err);
      return false;
    }
  }

  return false;
};

export {
  copyToClipboard,
  getDaysInMonth,
  isAfterPresentTime,
  isAfterPresentTimeAndToday,
  isAfterTomorrow,
  isBeforeToday,
  isImageURL,
  isToday,
  isTomorrow,
  meetingsByDate,
  rangeOfFullYears,
  rangeOfFullYearsWithFuture,
};
